/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-syntax */
import type { Collection, Key, Node } from 'react-stately';
import type {
  GetSize,
  LayoutHeader,
  LayoutInfo,
  LayoutItem,
  LayoutSection,
} from '../types';
import { maxY } from '../utils';

/**
 * Builds a nested list layout for react-aria defined collections,
 * with support for sections and items. The code here is heavily
 * inspired by the class based react-aria implementation found here:
 * https://github.com/adobe/react-spectrum/blob/109ea066ed6d953abd2ee2cd7f8051823ae7cf52/packages/%40react-stately/layout/src/ListLayout.ts#L196
 */

export function buildStackLayout<T>({
  collection,
  getSize,
  sectionGap = 0,
  paddingTop = 0,
  paddingBottom = 0,
  itemGap = 0,
}: {
  collection: Collection<Node<T>>;
  getSize: GetSize<T>;
  sectionGap?: number;
  paddingTop?: number;
  paddingBottom?: number;
  itemGap?: number;
}) {
  const layoutInfo = new Map<Key, LayoutInfo>();
  let y = paddingTop;
  const nodes: Array<LayoutInfo> = [];
  let index = 0;
  for (const node of collection) {
    index += 1;
    const layoutNode = buildNode(
      node,
      y,
      getSize,
      layoutInfo,
      collection,
      itemGap
    );
    const key = node.key;
    let gap = 0;

    if (node.type === 'section') {
      // @ts-expect-error FIXME(strictNullChecks)
      const hasNextSection = !!findNextSection(collection, node.nextKey);
      if (hasNextSection) {
        gap = sectionGap;
      }
      layoutNode.gapEnd = gap;
    } else if (index < collection.size) {
      gap = itemGap;
      // For items, keep the gapEnd that was set in buildNode
    }

    layoutInfo.set(key, layoutNode);
    y = maxY(layoutNode.rect) + gap;
    nodes.push(layoutNode);
  }

  const totalSize = y + paddingBottom;
  return {
    nodes,
    totalSize,
    layoutInfo,
  };
}

/**
 * Build a node in the layout tree, either a section or an item.
 */

function buildNode<T>(
  node: Node<T>,
  y: number,
  getSize: GetSize<T>,
  layoutInfo: Map<Key, LayoutInfo>,
  collection: Collection<Node<T>>,
  itemGap = 0
) {
  // @ts-expect-error FIXME(strictNullChecks)
  const nextNode = collection.getItem(node.nextKey);
  const gapEnd = nextNode?.type === 'item' ? itemGap : 0;

  // base item
  if (node.type === 'item') {
    const key = node.key;
    const { height, isMeasured } = getSize(key, node.type, node);
    const item: LayoutItem = {
      type: 'item',
      rect: { y, height, x: 0, width: 0 },
      isMeasured,
      key: node.key,
      // @ts-expect-error FIXME(strictNullChecks)
      parentKey: node.parentKey,
      gapEnd,
    };

    return item;
  }

  // create a header for the section
  const isHeaderVisible = node.props?.isHeaderVisible ?? true;
  const headerKey = `${node.key}:header`;
  const { height: headerHeight, isMeasured: headerMeasured } = getSize(
    headerKey,
    'header',
    node
  );

  const headerRect = {
    y,
    height: isHeaderVisible ? headerHeight : 0,
    x: 0,
    width: 0,
  };

  const header: LayoutHeader = {
    type: 'header',
    rect: headerRect,
    isMeasured: headerMeasured,
    key: headerKey,
    parentKey: node.key,
    isSticky: node.props?.isHeaderSticky ?? false,
    isVisible: isHeaderVisible,
  };

  layoutInfo.set(header.key, header);

  // create the seciton layout which combines all descendants,
  // including the header
  const children: LayoutInfo[] = [];
  const startY = y;
  const rect = { y, height: headerRect.height, x: 0, width: 0 };

  y += headerRect.height;

  for (const child of node.childNodes) {
    const layout = buildNode(
      child,
      y,
      getSize,
      layoutInfo,
      collection,
      itemGap
    );
    layoutInfo.set(child.key, layout);
    if (node.nextKey) {
      const nextChild = collection.getItem(node.nextKey);
      if (nextChild) {
        y = maxY(layout.rect) + (nextChild.type === 'item' ? itemGap : 0);
        children.push(layout);
      }
    }
  }

  rect.height = y - startY;

  const section: LayoutSection = {
    type: 'section',
    rect,
    key: node.key,
    // @ts-expect-error FIXME(strictNullChecks)
    parentKey: node.parentKey,
    item: header,
    children,
  };

  return section;
}

function findNextSection<T>(
  collection: Collection<Node<T>>,
  startKey: Key | null
) {
  let key = startKey;

  while (key != null) {
    const next = collection.getItem(key);
    if (!next) {
      break;
    }

    if (next.type === 'section') {
      return next;
    }

    // @ts-expect-error FIXME(strictNullChecks)
    key = next.nextKey;
  }

  return null;
}
